import React, { ChangeEvent, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Checkbox,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  Slide,
  BottomNavigation,
  Paper,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { TransitionProps } from "@mui/material/transitions";
import Unchecked from "../../assets/images/checkbox-unchecked.svg";
import Checked from "../../assets/images/checkbox-checked.png";
import DeleteIcon from "../../assets/images/delete-icon.svg";
import {
  DomSnapshot,
  NewEvent,
  SelectedDomsObj,
} from "../../declarations/interface";
import DomCard from "./DomCard";
import { reorder } from "../../util/UtilService";
import Preview from "../../components/SnapshotView/Preview";
import eventsCapture from "../../services/events-capture";
import AddIcon from "../../assets/images/add-icon-secondary.svg";
import TickIcon from "../../assets/images/tick-icon-green.svg";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "100%",
  },
  item: {
    height: "100%",
    "&:first-child": {
      borderRight: `0.5px solid ${theme.palette.primary40.main}`,
    },
    "&:last-child": {
      borderLeft: `0.5px solid ${theme.palette.primary40.main}`,
      background: theme.palette.background.previewBg,
    },
  },
  checkbox: {
    width: 16,
    height: 16,
  },
  domListContainer: {
    height: "calc(100% - 40px)",
    overflow: "auto",
    boxShadow: "0px -4px 5px -4px rgba(0, 0, 0, 0.15)",
  },
  stickyContainer: {
    display: "flex",
    alignItems: "center",
    height: "24px",
  },
  preview: {
    padding: `${theme.spacing(3)} ${theme.spacing(3)}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

const DomSelectionView: React.FC<{
  domList: DomSnapshot[];
  selectedDoms: SelectedDomsObj;
  selectDom: (dom: DomSnapshot, select: boolean) => void;
  selectAllDoms: (select: boolean) => void;
  selectDomsHavingEvents: (select: boolean) => void;
  setPreviewDom: (dom: DomSnapshot) => void;
  activeDom?: DomSnapshot;
  setActiveDom?: (dom: DomSnapshot) => void;
}> = ({
  domList,
  selectedDoms,
  selectDom,
  selectAllDoms,
  selectDomsHavingEvents,
  setPreviewDom,
  activeDom,
  setActiveDom,
}) => {
  const classes = useStyles();
  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    selectAllDoms(event.target.checked);
  };
  const handleSelectDomsHavingEvents = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    selectDomsHavingEvents(event.target.checked);
  };

  const selectedCount = () =>
    domList.filter((dom) => !!selectedDoms.data[dom.dom_uuid]).length;
  const isAllSelected = () =>
    domList.every((dom) => !!selectedDoms.data[dom.dom_uuid]) &&
    !!domList.length;
  const isDomsWithEventsSelected = () =>
    domList
      .filter((dom) => dom.events_lists.length)
      .every((dom) => !!selectedDoms.data[dom.dom_uuid]) &&
    !domList
      .filter((dom) => !dom.events_lists.length)
      .some((dom) => !!selectedDoms.data[dom.dom_uuid]) &&
    !!domList.length;

  return (
    <>
      <Box gap={2} my={2} px={4.5} className={classes.stickyContainer}>
        <Box>
          <Tooltip title={isAllSelected() ? "Deselect All" : "Select All"}>
            <Checkbox
              classes={{ root: classes.checkbox }}
              icon={<img src={Unchecked} width="18" height="18" alt="icon" />}
              checkedIcon={
                <img src={Checked} width="18" height="18" alt="icon" />
              }
              onChange={handleSelectAll}
              checked={isAllSelected()}
              inputProps={{
                "aria-label": "Select All Checkbox",
              }}
            />
          </Tooltip>
        </Box>
        <Box flexGrow={1}>
          <Box>
            <Typography variant="subtitle2">
              <b>ADD OR REMOVE SNAPSHOTS</b>
            </Typography>
            <Typography variant="h5" sx={{ marginTop: 0.25 }}>
              {selectedCount()} of {domList.length} Selected
            </Typography>
          </Box>
        </Box>
        <Box>
          <Tooltip
            title={
              isDomsWithEventsSelected()
                ? "Deselect All"
                : "Select Only Doms Having Events"
            }
          >
            <Checkbox
              classes={{ root: classes.checkbox }}
              icon={<img src={Unchecked} width="18" height="18" alt="icon" />}
              checkedIcon={
                <img src={Checked} width="18" height="18" alt="icon" />
              }
              onChange={handleSelectDomsHavingEvents}
              checked={isDomsWithEventsSelected()}
              inputProps={{
                "aria-label": "Select Only Doms Having Events",
              }}
            />
          </Tooltip>
        </Box>
        <Box flexGrow={1}>
          <Box>
            <Typography variant="subtitle2">
              <b>SELECT DOMS HAVING EVENTS</b>
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className={classes.domListContainer} px={4.5} py={2}>
        {domList.map((dom, index) => (
          <Box key={dom.dom_uuid} display="flex" gap={2}>
            <Box mt={5.25}>
              <Checkbox
                classes={{ root: classes.checkbox }}
                icon={<img src={Unchecked} width="18" height="18" alt="icon" />}
                checkedIcon={
                  <img src={Checked} width="18" height="18" alt="icon" />
                }
                onChange={() => {
                  selectDom(dom, !selectedDoms.data[dom.dom_uuid]);
                }}
                checked={!!selectedDoms.data[dom.dom_uuid]}
                inputProps={{
                  "aria-label": "Select Dom",
                }}
              />
            </Box>
            <Box flexGrow={1}>
              <DomCard
                id={dom.dom_uuid}
                eventsCount={dom.events_lists.length}
                name={dom.dom_uuid}
                hasNext={index < domList.length - 1}
                preview={dom.dom_thumbnail_path}
                active={activeDom?.dom_uuid === dom.dom_uuid}
                handlePreviewClick={() => {
                  setPreviewDom(dom);
                }}
                handleClick={() => {
                  setActiveDom && setActiveDom(dom);
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

const DraggableDomList: React.FC<{
  selectedDoms: SelectedDomsObj;
  setPreviewDom: (dom: DomSnapshot) => void;
  handleRemoveDom: (dom: DomSnapshot, select: boolean) => void;
  activeDom?: DomSnapshot;
  setActiveDom?: (dom: DomSnapshot) => void;
  reorderSelectedDoms?: (reorderedDomIds: string[]) => void;
}> = React.memo(
  ({
    selectedDoms,
    setPreviewDom,
    handleRemoveDom,
    activeDom,
    setActiveDom,
    reorderSelectedDoms,
  }) => {
    const classes = useStyles();
    const onDragEnd = ({ destination, source }: DropResult) => {
      if (!destination) return;
      const reorderedDomIds = reorder(
        [...selectedDoms.orderedDomIds],
        source.index,
        destination.index
      );
      reorderSelectedDoms && reorderSelectedDoms(reorderedDomIds);
    };
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable-list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {selectedDoms.orderedDomIds.map((dom_uuid, index) => (
                <Draggable draggableId={dom_uuid} index={index} key={dom_uuid}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <DomCard
                        id={selectedDoms.data[dom_uuid].dom_uuid}
                        eventsCount={
                          selectedDoms.data[dom_uuid].events_lists.length
                        }
                        name={selectedDoms.data[dom_uuid].dom_uuid}
                        hasNext={index < selectedDoms.orderedDomIds.length - 1}
                        preview={selectedDoms.data[dom_uuid].dom_thumbnail_path}
                        active={activeDom?.dom_uuid === dom_uuid}
                        handlePreviewClick={() => {
                          setPreviewDom(selectedDoms.data[dom_uuid]);
                        }}
                        handleRemoveClick={() => {
                          handleRemoveDom(selectedDoms.data[dom_uuid], false);
                        }}
                        handleClick={() => {
                          setActiveDom &&
                            setActiveDom(selectedDoms.data[dom_uuid]);
                        }}
                        isDragging={snapshot.isDragging}
                      />
                    </Box>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }
);

const SelectedDomsView: React.FC<{
  selectedDoms: SelectedDomsObj;
  setPreviewDom: (dom: DomSnapshot) => void;
  removeAllSelectedDoms: () => void;
  handleRemoveDom: (dom: DomSnapshot, select: boolean) => void;
  activeDom?: DomSnapshot;
  setActiveDom?: (dom: DomSnapshot) => void;
  reorderSelectedDoms?: (reorderedDomIds: string[]) => void;
}> = ({
  selectedDoms,
  setPreviewDom,
  removeAllSelectedDoms,
  handleRemoveDom,
  activeDom,
  setActiveDom,
  reorderSelectedDoms,
}) => {
  const classes = useStyles();

  return (
    <>
      <Box gap={2} my={2} px={4.5} className={classes.stickyContainer}>
        <Box>
          <Tooltip title={"Remove All"}>
            <div>
              <IconButton
                sx={{ padding: 0.5 }}
                onClick={removeAllSelectedDoms}
                data-testid="remove-all-selected-snapshots"
                disabled={selectedDoms.orderedDomIds.length === 0}
              >
                <img src={DeleteIcon} alt="close" height="24" width="24" />
              </IconButton>
            </div>
          </Tooltip>
        </Box>
        <Box flexGrow={1}>
          <Box>
            <Typography variant="subtitle2">
              MANAGE NEW RECORDING: <b>ADD OR REMOVE SNAPSHOTS</b>
            </Typography>
            <Typography variant="h5" sx={{ marginTop: 0.25 }}>
              Total snapshots Selected: {selectedDoms.orderedDomIds.length}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className={classes.domListContainer} px={4.5} py={2}>
        <DraggableDomList
          selectedDoms={selectedDoms}
          setPreviewDom={setPreviewDom}
          handleRemoveDom={handleRemoveDom}
          setActiveDom={setActiveDom}
          activeDom={activeDom}
          reorderSelectedDoms={reorderSelectedDoms}
        />
      </Box>
    </>
  );
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PreviewWithNewEvents: React.FC<{
  domPath: string;
  open: boolean;
  events: NewEvent[];
  handleClose: (events: NewEvent[]) => void;
}> = ({ domPath, open, events, handleClose }) => {
  const [htmlContent, setHtmlContent] = useState("");
  const [newEvents, setNewEvents] = useState(events);
  const [listening, setListening] = useState(false);

  useEffect(() => {
    const fetchDom = async () => {
      const domPathResponse = await fetch(domPath, { method: "GET" });
      const htmlText = await domPathResponse.text();
      setHtmlContent(htmlText);
    };
    fetchDom();
  }, [domPath]);

  useEffect(() => {
    setNewEvents(events);
  }, [events]);

  useEffect(() => {
    setTimeout(() => {
      if (open) {
        const container = document.getElementById("dom-preview-html-content");
        if (container) {
          container.addEventListener("pointerdown", handleClick);
          container.addEventListener("click", (event: any) => {
            event.preventDefault();
          });
          setListening(true);
          return () => {
            container.removeEventListener("pointerdown", handleClick);
          };
        }
      }
    }, 100);
  }, [open]);

  const getHtmlAttributes = () => {
    try {
      const input = htmlContent.split(">")[0].split("html ")[1];
      const output: any = {};
      const regex = /(\w+)="([^"]*)"/g;
      let match;
      while ((match = regex.exec(input)) !== null) {
        const key = match[1];
        const value = match[2];
        output[key] = value;
      }
      return output;
    } catch (error) {
      console.log(error);
      return {};
    }
  };

  const handleClick = (event: any) => {
    const eventPayload = eventsCapture.getEventPayload(event);
    console.log("Clicked element,", eventPayload);
    setNewEvents((prev) => {
      const { eventId: e2 } = eventPayload;
      const prevEvent = prev.find((e) => {
        const { eventId: e1 } = e;
        return `${e1}` === `${e2}`;
      });
      if (prevEvent) {
        return prev.filter((e) => {
          const { eventId: e1 } = e;
          return `${e1}` !== `${e2}`;
        });
      } else {
        return [...prev, eventPayload];
      }
    });
    event.preventDefault();
  };

  return (
    <React.Fragment>
      <Dialog
        fullScreen
        open={open}
        onClose={() => {
          handleClose(newEvents);
        }}
        TransitionComponent={Transition}
      >
        {htmlContent.length && (
          <div id="dom-preview-html-content">
            <html
              {...getHtmlAttributes()}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>
        )}
        {listening && (
          <Paper
            sx={{
              position: "fixed",
              bottom: "7%",
              left: "40%",
              right: "40%",
            }}
            elevation={3}
          >
            <Box>
              <BottomNavigation
                showLabels
                style={{ height: "unset", borderRadius: "4px" }}
              >
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  gap={2}
                  justifyContent={"space-between"}
                  width={"100%"}
                  px={1}
                >
                  <Typography
                    sx={{ ml: "auto", flex: 1 }}
                    variant="h4"
                    component="div"
                  >
                    New events: {newEvents.length}
                  </Typography>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={() => {
                      handleClose(newEvents);
                    }}
                    aria-label="tick"
                  >
                    <img src={TickIcon} alt="tick" height="24" width="24" />
                  </IconButton>
                </Box>
              </BottomNavigation>
            </Box>
          </Paper>
        )}
      </Dialog>
    </React.Fragment>
  );
};

const UtilityWindow: React.FC<{
  activeDom: DomSnapshot;
  updateEvents: (newEvents: NewEvent[], activeDom: DomSnapshot) => void;
}> = ({ activeDom, updateEvents }) => {
  const classes = useStyles();
  const [events, setEvents] = useState<NewEvent[]>([]);
  const [open, setOpen] = useState(false);
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = (newEvents: NewEvent[]) => {
    setOpen(false);
    updateEvents(newEvents, activeDom);
  };

  useEffect(() => {
    let eventsList = [...activeDom.events_lists] as NewEvent[];
    if (activeDom.new_events) {
      eventsList = [...eventsList, ...activeDom.new_events];
    }
    setEvents(eventsList);
  }, [activeDom]);
  return (
    <>
      <PreviewWithNewEvents
        domPath={activeDom.dom_path}
        open={open}
        handleClose={handleClose}
        events={activeDom.new_events ?? []}
      />
      <Box gap={2} my={2} px={4.5} className={classes.stickyContainer}>
        <Box>
          <Tooltip title={"Manage events"}>
            <div>
              <IconButton
                sx={{ padding: 0.5 }}
                onClick={handleClickOpen}
                data-testid="manage-events"
              >
                <img src={AddIcon} alt="close" height="24" width="24" />
              </IconButton>
            </div>
          </Tooltip>
        </Box>
        <Box flexGrow={1}>
          <Box>
            <Typography variant="subtitle2">
              MANAGE NEW EVENTS: <b>ADD OR REMOVE EVENTS</b>
            </Typography>
            <Typography variant="h5" sx={{ marginTop: 0.25 }}>
              Total events: {events.length} | New events:{" "}
              {activeDom.new_events?.length ?? 0}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className={classes.domListContainer} px={4.5} py={2}>
        <Box textAlign="center">
          <Preview
            viewType={"FLOW_VIEW"}
            image={activeDom.dom_thumbnail_path}
            wait_type={"EVENT"}
            hotSpotData={{
              height: activeDom.height ?? 0,
              width: activeDom.width ?? 0,
              events: events,
            }}
            onMapEvent={(eventId: string) => {
              console.log(eventId);
            }}
          />
        </Box>
      </Box>
    </>
  );
};

const CreateNewRecordingBody: React.FC<{
  domList: DomSnapshot[];
  selectedDoms: SelectedDomsObj;
  selectDom: (dom: DomSnapshot, select: boolean) => void;
  selectAllDoms: (select: boolean) => void;
  selectDomsHavingEvents: (select: boolean) => void;
  setPreviewDom: (dom: DomSnapshot) => void;
  removeAllSelectedDoms: () => void;
  activeDom?: DomSnapshot;
  setActiveDom?: (dom: DomSnapshot) => void;
  reorderSelectedDoms?: (reorderedDomIds: string[]) => void;
  updateEvents: (newEvents: NewEvent[], activeDom: DomSnapshot) => void;
}> = ({
  domList,
  selectedDoms,
  selectDom,
  selectAllDoms,
  selectDomsHavingEvents,
  setPreviewDom,
  removeAllSelectedDoms,
  activeDom,
  setActiveDom,
  reorderSelectedDoms,
  updateEvents,
}) => {
  const classes = useStyles();
  return (
    <>
      <Grid
        container
        classes={{ container: classes.container }}
        sx={{
          height: "100%",
        }}
      >
        <Grid item xs={4} classes={{ item: classes.item }} py={2}>
          <DomSelectionView
            domList={domList}
            selectedDoms={selectedDoms}
            selectDom={selectDom}
            selectAllDoms={selectAllDoms}
            selectDomsHavingEvents={selectDomsHavingEvents}
            setPreviewDom={setPreviewDom}
            activeDom={activeDom}
            setActiveDom={setActiveDom}
          />
        </Grid>
        <Grid item xs={4} classes={{ item: classes.item }} py={2}>
          <SelectedDomsView
            selectedDoms={selectedDoms}
            setPreviewDom={setPreviewDom}
            removeAllSelectedDoms={removeAllSelectedDoms}
            handleRemoveDom={selectDom}
            activeDom={activeDom}
            setActiveDom={setActiveDom}
            reorderSelectedDoms={reorderSelectedDoms}
          />
        </Grid>
        <Grid item xs={4} classes={{ item: classes.item }} py={2}>
          {activeDom && (
            <UtilityWindow activeDom={activeDom} updateEvents={updateEvents} />
          )}
        </Grid>
      </Grid>
    </>
  );
};

CreateNewRecordingBody.propTypes = {
  domList: PropTypes.any.isRequired,
  selectedDoms: PropTypes.any.isRequired,
  selectDom: PropTypes.func.isRequired,
  selectAllDoms: PropTypes.func.isRequired,
  selectDomsHavingEvents: PropTypes.func.isRequired,
  setPreviewDom: PropTypes.func.isRequired,
  removeAllSelectedDoms: PropTypes.func.isRequired,
  activeDom: PropTypes.any,
  setActiveDom: PropTypes.func,
  reorderSelectedDoms: PropTypes.func,
};

DraggableDomList.propTypes = {
  selectedDoms: PropTypes.any.isRequired,
  setPreviewDom: PropTypes.func.isRequired,
  activeDom: PropTypes.any,
  setActiveDom: PropTypes.func,
  handleRemoveDom: PropTypes.func.isRequired,
  reorderSelectedDoms: PropTypes.func.isRequired,
};
DraggableDomList.displayName = "DraggableDomList";

export default CreateNewRecordingBody;
